import type { CenterProps, MotionPropsFor } from '@hover/blueprint';
import { Center, Motion, forwardRef, motion } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import type { AnimatedEllipsisProps } from './AnimatedEllipsis';
import { AnimatedEllipsis } from './AnimatedEllipsis';

export interface LoadingStateProps
  extends Pick<AnimatedEllipsisProps, 'color' | 'label'>,
    MotionPropsFor<CenterProps> {
  /**
   * Controls whether the loading state is active. When set to false, this
   * component won't render anything.
   */
  isLoading?: boolean;
  children?: never;
  loaderProps?: Partial<AnimatedEllipsisProps>;
}

const MotionCenter = motion(Center);

/**
 * Alternative version of `LoadingState` from the design system that uses the
 * `AnimatedEllipsis` component to indicate loading while we wait to upstream
 * `AnimatedEllipsis` in place of `Loader`.
 *
 * @todo(design-system) Once we've replaced `Loader` with `AnimatedEllipsis` in
 * the design system, we can just use `LoadingState` from the system.
 */
const LoadingState = forwardRef<LoadingStateProps, 'div'>(
  (
    {
      color,
      isLoading,
      label,
      loaderProps = { width: '200', height: 'auto' },
      ...props
    },
    ref,
  ) => (
    <Motion.Firm>
      <AnimatePresence>
        {isLoading && (
          <MotionCenter
            animate={{ opacity: 1 }}
            background="overlayLight"
            bottom="0"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            left="0"
            margin="0 !important"
            position="absolute"
            ref={ref}
            right="0"
            rounded="inherit"
            top="0"
            zIndex={10}
            {...props}
          >
            <AnimatedEllipsis color={color} label={label} {...loaderProps} />
          </MotionCenter>
        )}
      </AnimatePresence>
    </Motion.Firm>
  ),
);

LoadingState.displayName = 'LoadingState';

export { LoadingState };
